import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useApi from './useApi';
import { setInstoreVccFlow, setupToken } from '../redux/actions';
import { segmentEvent, setRetailerContext } from '../helpers/sendEvent';
import fullStoryInstoreVccInit from '../helpers/fullStoryInstoreVcc';

export function parseStoreId(value) {
  if (!/^\d+$/.test(value || '')) {
    return null;
  }
  const storeId = Number(value);
  return storeId >= 1 ? storeId : null;
}

// retailer_id is a numeric id. Anything else (non-digits, leading zero, huge) is dropped, not stored.
export function sanitizeRetailerId(value) {
  const id = String(value ?? '');
  return /^[1-9]\d{0,9}$/.test(id) ? id : null;
}

// dba is display text: letters, digits, spaces and common business punctuation, capped at 100 characters.
export function sanitizeRetailerName(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const name = value
    .replace(/[^\p{L}\p{N} .,'&()/-]/gu, '')
    .trim()
    .slice(0, 100);
  return name || null;
}

// store_username is an identifier typed by retailers: letters, digits and . _ @ - only, capped at 100 characters.
export function sanitizeStoreUsername(value) {
  if (typeof value !== 'string') {
    return null;
  }
  const username = value
    .replace(/[^\p{L}\p{N}._@-]/gu, '')
    .trim()
    .slice(0, 100);
  return username || null;
}

// Resolves ?flow=instore_vcc&store_id=<id> into the in-store token, retailer public token and logo.
// `storeIdParam` is null while the flow is not active. Returns `failed: true` when the store cannot be
// resolved, so the caller (which renders the router) can send the customer to /error.
// `trackScan` is true only for the landing lookup: that is the moment the QR code was scanned. Later lookups
// (a token refresh on the card screen) are not scans.
export default function useInstoreVccInit(storeIdParam, trackScan = false) {
  const { request } = useApi();
  const dispatch = useDispatch();
  const [failed, setFailed] = useState(false);
  // `request` is a new object on every render, so it must not be an effect dependency (the lookup would
  // re-run each render). The ref always holds the latest one.
  const requestRef = useRef(request);
  requestRef.current = request;

  useEffect(() => {
    if (storeIdParam === null) {
      return undefined;
    }
    // Set on cleanup (param changed or unmounted) so a slower, earlier lookup cannot overwrite a newer one.
    let cancelled = false;
    const storeId = parseStoreId(storeIdParam);
    if (!storeId) {
      setFailed(true);
      return undefined;
    }
    const init = async () => {
      let res;
      try {
        res = await requestRef.current.instoreLocationRequest(storeId);
      } catch (e) {
        res = null;
      }
      if (cancelled) {
        return;
      }
      if (!res?.ok || !res.token) {
        setFailed(true);
        return;
      }
      // sendEvent.js adds these to every Segment event. Katapult.js only sets them from the postMessage checkout,
      // which the in-store flow never receives. The response is untrusted, so only validated values are kept.
      // store_id feeds the Fullstory identity and sessionStorage, so it is validated like the request value.
      const resolvedStoreId = parseStoreId(String(res.store_id ?? '')) || storeId;
      const retailerId = sanitizeRetailerId(res.retailer_id);
      const retailerName = sanitizeRetailerName(res.dba);
      const storeUsername = sanitizeStoreUsername(res.store_username);
      setRetailerContext({ retailerId, retailerName });
      fullStoryInstoreVccInit({
        retailerId,
        retailerName,
        storeId: resolvedStoreId,
        storeUsername,
      });
      dispatch(
        setInstoreVccFlow({
          storeId: resolvedStoreId,
          token: res.token,
          tokenType: res.token_type,
          expiresAt: res.expires_at,
          logoUrl: res.logo_url || null,
          dba: retailerName,
          publicToken: res.public_token,
        }),
      );
      if (trackScan) {
        segmentEvent('track', 'QR Code Scanned', {
          flow: 'instore_vcc',
          retailer_id: res.retailer_id,
          store_id: resolvedStoreId,
          store_username: storeUsername,
          store_address: res.store_address,
        });
      }
      // The retailer public token is the Authorization for every request the in-store flow makes through
      // useApi / useFirstPayment, which read technical.initialize.token (default '3c007be61d17302fd4e49ff26e2ed471f844dab4').
      dispatch(setupToken(res.public_token));
    };
    init().catch(() => {
      if (!cancelled) {
        setFailed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [storeIdParam, dispatch]);

  return { failed };
}
